import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import host from "../../global.jsx";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import Loader from "../Loader.jsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import DiscardChangesModal from "../shared/DiscardChangesModal";
import SetDetailsCard, { DEFAULT_CONFIG } from "./behavioral/manual/SetDetailsCard";
import QuestionListPanel from "./behavioral/manual/QuestionListPanel";
import BuilderSidebar from "./behavioral/manual/BuilderSidebar";
import { MIN_QUESTIONS_PER_COMPETENCY, MIN_COMPETENCIES, newQuestion } from "./behavioral/manual/QuestionEditor";
import PartialFailureBanner from "./behavioral/ai/PartialFailureBanner";
import StepIndicator from "./mcq/StepIndicator";

// Duplicated from AIGenerateFlow.jsx, not imported — same pattern as RowMenu/
// SortIcon elsewhere in this codebase, avoids pulling in the whole AI flow module.
const AI_STEPS = ["Requirements", "Review Allocation", "Edit & Save"];

// Per-set, not a single shared slot — otherwise opening a second set while the
// first has unsaved edits silently wipes the first one's draft the moment this
// component mounts for the second (the load effect's cleanup-on-mismatch ran
// against one shared key for every set).
const draftKey = (id) => `behavioral_set_draft_${id ?? "new"}`;

const emptyDraft = () => ({
  name: "",
  description: "",
  questions: [],
  config: { ...DEFAULT_CONFIG },
});

/** Blocking rules, mirroring the backend's _validate_behavioral_set. `competencies`
 * is the current real list — a competency_id that doesn't resolve against it (a
 * recovered draft, or one the platform list no longer has) must not silently count
 * toward coverage while showing an empty dropdown. */
const validateDraft = (draft, competencies) => {
  const errors = {};
  const perCompetency = {};
  const validCompetencyIds = new Set((competencies || []).map((c) => c.id));

  draft.questions.forEach((q) => {
    const texts = q.options.map((o) => (o.text || "").trim());
    const hasValidCompetency = q.competency_id && validCompetencyIds.has(q.competency_id);
    if (!(q.scenario_text || "").trim()) errors[q.id] = "Scenario text is required";
    else if (!hasValidCompetency) errors[q.id] = "Assign a competency";
    else if (texts.some((t) => !t)) errors[q.id] = "Every option needs text";
    else if (new Set(texts.map((t) => t.toLowerCase())).size !== texts.length)
      errors[q.id] = "Options must be distinct";

    if (hasValidCompetency) {
      perCompetency[q.competency_id] = (perCompetency[q.competency_id] || 0) + 1;
    }
  });

  return { errors, perCompetency };
};

/**
 * Builds one Behavioural set — structurally mirrors CreateMCQSet.jsx (same
 * draft/sessionStorage-recovery/whole-document-save shape). A brand-new draft starts
 * pre-loaded with the platform's 15 default questions rather than empty (proposal
 * §1) — nothing is written until Save, same as MCQ's own unsaved-draft behavior.
 */
const CreateBehavioralSet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setId } = useParams();
  const accessToken = Cookies.get("accessToken");
  const authHeaders = { Authorization: `Bearer ${accessToken}` };

  // AI drafts get their own storage slot ("ai-new"), separate from manual ones
  // ("new") — otherwise an abandoned AI draft leaks into the next "Start from Defaults".
  const draftSlot = setId ?? (location.state?.aiDraft ? "ai-new" : "new");

  const [sets, setSets] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);

  // AI-guided hand-off (behavioral-test-phase2-ai-guided-proposal.md). All local —
  // reviewAcknowledged is never written to draft/save payload; aiRequestContext only
  // exists to ground a single-competency retry with the same role/JD the original
  // generation used.
  const [failedCompetencies, setFailedCompetencies] = useState([]);
  const [retryingCompetencyId, setRetryingCompetencyId] = useState(null);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const [aiRequestContext, setAiRequestContext] = useState(null);

  const fetchSets = async () => {
    try {
      const res = await axios.get(`${host}/behavioral-sets`, { headers: authHeaders });
      setSets(res.data.sets || []);
    } catch {
      /* the warning is a nicety; failing to load names must not block editing */
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      await fetchSets();

      let defaults = { competencies: [], questions: [] };
      try {
        const res = await axios.get(`${host}/behavioral-defaults`, { headers: authHeaders });
        defaults = res.data;
      } catch {
        toast.error("Could not load competencies");
      }
      if (cancelled) return;
      setCompetencies(defaults.competencies || []);

      // AI-guided hand-off via location.state, takes priority over !setId below.
      // sessionStorage's "ai-new" slot is checked first in case a browser-back
      // restored this state after edits were already made (avoids reverting them).
      const aiDraft = location.state?.aiDraft;
      if (!setId && aiDraft) {
        let resumedDraft = aiDraft;
        let resumedFailed = location.state?.failedCompetencies || [];
        let resumedContext = location.state?.aiRequestContext || null;
        try {
          const saved = JSON.parse(sessionStorage.getItem(draftKey("ai-new")) || "null");
          if (saved?.draft?.questions?.length) {
            resumedDraft = saved.draft;
            resumedFailed = saved.failedCompetencies || [];
            resumedContext = saved.aiRequestContext || null;
          }
        } catch {
          sessionStorage.removeItem(draftKey("ai-new"));
        }
        setDraft(resumedDraft);
        setFailedCompetencies(resumedFailed);
        setAiRequestContext(resumedContext);
        setReviewAcknowledged(false);
        // Writes to the "ai-new" slot via the persistence effect below.
        setIsDirty(true);
        setAttemptedSave(false);
        setIsLoading(false);
        return;
      }

      let loaded;
      if (!setId) {
        loaded = {
          ...emptyDraft(),
          questions: (defaults.questions || []).map((q) => ({
            id: `q_${crypto.randomUUID()}`,
            competency_id: q.competency_id,
            scenario_text: q.scenario_text,
            options: (q.options || []).map((o) => ({
              id: `o_${crypto.randomUUID()}`,
              text: o.text,
              rank: o.rank,
            })),
          })),
        };
      } else {
        try {
          const res = await axios.get(`${host}/behavioral-sets/${setId}`, { headers: authHeaders });
          const doc = res.data;
          loaded = {
            name: doc.name || "",
            description: doc.description || "",
            questions: doc.questions || [],
            config: { ...DEFAULT_CONFIG, ...(doc.config || {}) },
          };
        } catch {
          if (!cancelled) {
            toast.error("Could not load that set");
            navigate("/org-admin/behavioralsets");
          }
          return;
        }
      }
      if (cancelled) return;

      // Recovery mirrors CreateMCQSet.jsx's shape — the stored id must match the set
      // being edited, or one set's unsaved work would restore itself over another.
      // One addition MCQ's own recovery doesn't need: a blank draft (no name, no
      // scenario/option text typed anywhere) is worthless and must not win over a
      // fresh set's real 15-default-question prefill — otherwise a draft saved by
      // opening this page and immediately navigating away permanently hides the
      // defaults behind a "recovered" empty form on every future visit.
      const hasRealContent = (d) =>
        !!(d?.name || "").trim() ||
        (d?.questions || []).some(
          (q) =>
            (q.scenario_text || "").trim() ||
            (q.options || []).some((o) => (o.text || "").trim())
        );

      let recovered = false;
      try {
        const saved = JSON.parse(sessionStorage.getItem(draftKey(draftSlot)) || "null");
        if (saved?.draft && hasRealContent(saved.draft)) {
          loaded = saved.draft;
          recovered = true;
          setFailedCompetencies(saved.failedCompetencies || []);
          setAiRequestContext(saved.aiRequestContext || null);
        } else if (saved) {
          sessionStorage.removeItem(draftKey(draftSlot));
        }
      } catch {
        sessionStorage.removeItem(draftKey(draftSlot));
      }

      setDraft(loaded);
      setIsDirty(recovered);
      setAttemptedSave(false);
      setIsLoading(false);
      if (recovered) toast.info("Recovered your unsaved set");
    };

    load();
    return () => { cancelled = true; };
  }, [setId]);

  useEffect(() => {
    if (!draft || isLoading || !isDirty) return;
    sessionStorage.setItem(draftKey(draftSlot), JSON.stringify({
      setId: setId ?? null, draft, failedCompetencies, aiRequestContext,
    }));
  }, [draft, draftSlot, setId, isLoading, isDirty, failedCompetencies, aiRequestContext]);

  const handleDraftChange = (next) => {
    setDraft(next);
    setIsDirty(true);
  };

  const { errors: questionErrors, perCompetency } = useMemo(
    () => (draft ? validateDraft(draft, competencies) : { errors: {}, perCompetency: {} }),
    [draft, competencies]
  );
  const visibleErrors = attemptedSave ? questionErrors : {};

  const nameWarning = useMemo(() => {
    if (!draft?.name?.trim()) return "";
    const clash = sets.some(
      (s) => s.id !== setId &&
        s.name.trim().toLowerCase() === draft.name.trim().toLowerCase()
    );
    return clash ? "Another set already uses this name" : "";
  }, [draft, sets, setId]);

  const jumpToQuestion = (questionId) => {
    setExpandedId(questionId);
    requestAnimationFrame(() => {
      document
        .getElementById(`behavioral-q-${questionId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Coverage rule mirrors the backend exactly — >=3 questions per assessed
  // competency, >=2 competencies overall.
  const coverageError = useMemo(() => {
    const short = Object.entries(perCompetency).filter(([, n]) => n < MIN_QUESTIONS_PER_COMPETENCY);
    if (short.length) return `Each assessed competency needs at least ${MIN_QUESTIONS_PER_COMPETENCY} questions to generate a reliable score`;
    if (Object.keys(perCompetency).length < MIN_COMPETENCIES) return `A set needs at least ${MIN_COMPETENCIES} competencies represented`;
    return "";
  }, [perCompetency]);

  const handleSave = async () => {
    if (!draft) return;
    setAttemptedSave(true);
    if (!draft.name.trim()) { toast.error("Set name is required"); return; }
    if (!draft.questions.length) { toast.error("Add at least one question"); return; }
    const errorCount = Object.keys(questionErrors).length;
    if (errorCount) {
      toast.error("Some fixes required");
      return;
    }
    if (coverageError) { toast.error(coverageError); return; }
    if (draft.source === "ai_guided" && !reviewAcknowledged) {
      toast.error("Confirm you've reviewed the AI-generated questions");
      return;
    }

    setIsSaving(true);
    // reviewAcknowledged is local-only UI state (never part of `draft` itself, and
    // never persisted through sessionStorage recovery — see its declaration above) —
    // it has to be added onto the outgoing payload explicitly here, since the
    // backend's own gate (_validate_behavioral_set) checks payload.reviewed, not
    // anything client-side.
    const payload = { ...draft, reviewed: reviewAcknowledged };
    try {
      if (setId) {
        await axios.put(`${host}/behavioral-sets/${setId}`, payload, {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        });
        toast.success("Set saved");
      } else {
        await axios.post(`${host}/behavioral-sets`, payload, {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        });
        toast.success("Set created");
      }
      sessionStorage.removeItem(draftKey(draftSlot));
      setIsDirty(false);
      navigate("/org-admin/behavioralsets");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving set");
    } finally {
      setIsSaving(false);
    }
  };

  const leave = () => {
    sessionStorage.removeItem(draftKey(draftSlot));
    navigate("/org-admin/behavioralsets");
  };

  const handleRetryCompetency = async (competencyId) => {
    const entry = failedCompetencies.find((f) => f.competency_id === competencyId);
    if (!entry) return;
    setRetryingCompetencyId(competencyId);
    try {
      const res = await axios.post(
        `${host}/behavioral-sets/generate`,
        { ...aiRequestContext, allocation: [{ competency_id: competencyId, count: entry.count || MIN_QUESTIONS_PER_COMPETENCY }] },
        { headers: authHeaders }
      );
      if (res.data.total_failure) {
        toast.error(`${entry.competency_name} failed again — try adding questions manually instead`);
        return;
      }
      handleDraftChange({ ...draft, questions: [...draft.questions, ...(res.data.questions || [])] });
      setFailedCompetencies((prev) => prev.filter((f) => f.competency_id !== competencyId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingCompetencyId(null);
    }
  };

  const handleAddManuallyForCompetency = (competencyId) => {
    const blanks = Array.from({ length: MIN_QUESTIONS_PER_COMPETENCY }, () => newQuestion(competencyId));
    handleDraftChange({ ...draft, questions: [...draft.questions, ...blanks] });
    setFailedCompetencies((prev) => prev.filter((f) => f.competency_id !== competencyId));
  };

  const handleBack = () => (isDirty ? setShowDiscard(true) : leave());

  if (isLoading || !draft) return <Loader />;

  const saveBlockedReason = !draft.name.trim()
    ? "Add a set name"
    : !draft.questions.length
    ? "Add at least one question"
    : coverageError
    ? coverageError
    : draft.source === "ai_guided" && !reviewAcknowledged
    ? "Confirm you've reviewed the AI-generated questions"
    : "";

  return (
    <>
      <div className="h-full">
        <div className="bg-bgPrimary flex flex-col min-h-full border border-gray-700/50 rounded-2xl pt-4 pb-6">
          <div className="flex items-center justify-between gap-4 px-6 pt-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0"
              >
                <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
              </button>
              <span className="dark:text-gray-400 text-textSecondary">
                {setId ? "Edit Behavioural Set" : "New Behavioural Set"}
              </span>
            </div>
          </div>

          {/* Continues the AI-guided flow's own step indicator (AIGenerateFlow.jsx)
              onto this page as step 3 of 3, so the journey reads as one continuous
              wizard instead of the indicator vanishing on navigate. Not clickable —
              steps 1-2 live on a different route, and there's no "back" action that
              makes sense from here. Absent entirely for the manual/default flow. */}
          {draft.source === "ai_guided" && (
            <StepIndicator steps={AI_STEPS} currentStep={3} />
          )}

          <PartialFailureBanner
            failedCompetencies={failedCompetencies}
            retryingId={retryingCompetencyId}
            onRetry={handleRetryCompetency}
            onAddManually={handleAddManuallyForCompetency}
          />

          <div className="w-full flex-1 mt-4 px-6 lg:px-12 flex flex-col gap-5">
            <SetDetailsCard draft={draft} nameWarning={nameWarning} onDraftChange={handleDraftChange} />

            <div className="flex flex-1 flex-col lg:flex-row gap-6 items-start lg:items-stretch">
              <QuestionListPanel
                questions={draft.questions}
                competencies={competencies}
                errors={visibleErrors}
                onChange={(questions) => handleDraftChange({ ...draft, questions })}
                expandedId={expandedId}
                onExpandedChange={setExpandedId}
              />

              <BuilderSidebar
                questions={draft.questions}
                competencies={competencies}
                perCompetency={perCompetency}
                errors={visibleErrors}
                config={draft.config}
                onJumpTo={jumpToQuestion}
                expandedId={expandedId}
                saveLabel={setId ? "Save Changes" : "Create Set"}
                saveBlockedReason={saveBlockedReason}
                isSaving={isSaving}
                isDirty={isDirty}
                onSave={handleSave}
                showReviewGate={draft.source === "ai_guided"}
                reviewAcknowledged={reviewAcknowledged}
                onReviewAcknowledgedChange={setReviewAcknowledged}
              />
            </div>
          </div>
        </div>
      </div>

      <DiscardChangesModal
        isOpen={showDiscard}
        onCancel={() => setShowDiscard(false)}
        onConfirm={() => { setShowDiscard(false); leave(); }}
      />
    </>
  );
};

export default CreateBehavioralSet;
